import { getPinComponentFragment } from '@lessonup/editor-shared';
import {
  AreaPinComponentWrapper,
  Editor,
  FillInTheBlanksWordBank,
  FillInTheBlanksWordBankBlank,
  FillInTheBlanksWordBankDistractor,
  FillInTheBlanksWordBankItem,
  TipTapComponentPropsWithScale,
  TipTapEditorContent,
  TipTapWrapper,
  useTipTapEditor,
  useUpdatePin,
  WordBankWrapper,
} from '@lessonup/pin-renderer';
import {
  FillInTheBlanksAreaPinComponentSettings,
  fillInTheBlanksBaseExtension,
  randomId,
  seededWordbankOrder,
  tipTapFillInTheBlanksBaseExtensions,
} from '@lessonup/pins-shared';
import {
  Button,
  createModal,
  cssBase,
  cx,
  InputValidation,
  ModalPopover,
  ModalPopoverProps,
  show,
  SpaceBetween,
  spacing,
  TextField,
  useForm,
  useModal,
} from '@lessonup/ui-components';
import { Collaboration, isChangeOrigin } from '@tiptap/extension-collaboration';
import { UniqueID } from '@tiptap/extension-unique-id';
import { TFunction } from 'i18next';
import { compact, debounce, isEqual, isNull } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { XmlFragment as YXmlFragment } from 'yjs';
import { useEditorTranslation } from '../../../hooks/useEditorTranslation';
import { teacherTextEditorStore } from '../EditorYjs/teacherTextEditorStore';
import { useAnyYjs } from '../YjsContext';
import { PasteHandler } from '../extensions/PasteHandler';
import { FillInTheBlanksContextProvider } from './FillInTheBlanksContext';
import { fillIntheBlanksEditorExtension } from './fillIntheBlanksEditorExtension';

export const TipTapFillInTheBlanks: React.FC<
  TipTapComponentPropsWithScale & { settings: FillInTheBlanksAreaPinComponentSettings; isPreviewMode?: boolean }
> = (props) => {
  const { pinId, pinComponentId, fragmentKey = 'f0' } = props;
  const { yDoc } = useAnyYjs();
  const fragment = useMemo(
    () => getPinComponentFragment(yDoc, pinId, pinComponentId, fragmentKey),
    [yDoc, pinId, pinComponentId, fragmentKey]
  );
  if (isNull(fragment)) return null;

  return <TipTapFillInTheBlanksInner fragment={fragment} {...props} />;
};

interface TipTapComponentInnerProps extends TipTapComponentPropsWithScale {
  fragment: YXmlFragment;
  settings: FillInTheBlanksAreaPinComponentSettings;
  isPreviewMode?: boolean;
}

export const TipTapFillInTheBlanksInner: React.FC<TipTapComponentInnerProps> = ({
  pinId,
  pinComponentId,
  active,
  editable,
  isTextEditMode,
  fragment,
  registerEditor,
  settings,
  isPreviewMode,
}) => {
  const { t } = useEditorTranslation();
  const [blanks, setBlanks] = useState<FillInTheBlanksWordBankBlank[]>([]);
  const [sellectedId, setSellectedId] = useState<string | undefined>(undefined);
  const [hoveredId, setHoveredId] = useState<string | undefined>(undefined);
  const { collaborationCursor, yDoc } = useAnyYjs();

  const debouncedUpdate = debounce(() => {
    teacherTextEditorStore.forceEditorUpdate();
  }, 50);

  const handleMouseDown = (event: React.MouseEvent) => {
    // do not move the component when in detail mode
    if (isTextEditMode) event.stopPropagation();
  };

  const handleWordClick = (word: FillInTheBlanksWordBankItem) => {
    if (word.type === 'blank') {
      // find the node
      let foundPos = -1;
      editor?.state.doc.descendants((node, pos) => {
        if (node.type.name === fillInTheBlanksBaseExtension.name && node.attrs.id === word.id) {
          foundPos = pos;
          return false;
        }
        return true;
      });
      // set the cursor to the word
      if (foundPos !== -1)
        editor
          ?.chain()
          .focus()
          .setNodeSelection(foundPos + 1)
          .run();
    }
  };

  const editor = useTipTapEditor(
    {
      immediatelyRender: false,
      editable: true,
      onTransaction: () => {
        debouncedUpdate();
      },
      onUpdate: ({ editor }: { editor: Editor }) => {
        const fillInTheBlanksNodes: FillInTheBlanksWordBankBlank[] = [];
        editor.state.doc.descendants((node) => {
          if (node.type.name === fillInTheBlanksBaseExtension.name) {
            fillInTheBlanksNodes.push({
              type: 'blank',
              id: node.attrs.id,
              word: node.toJSON(),
            });
            return false;
          }
          return true;
        });
        setBlanks((prev) => {
          if (isEqual(prev, fillInTheBlanksNodes)) return prev;
          return fillInTheBlanksNodes;
        });
      },
      extensions: compact([
        ...tipTapFillInTheBlanksBaseExtensions,
        PasteHandler,
        Collaboration.extend({
          // We have to override undo and redo from tiptap, since we already have the undo manager directly integrated in our editor
          addCommands() {
            return {
              undo: () => () => {
                return false;
              },
              redo: () => () => {
                return false;
              },
            };
          },
        }).configure({
          document: yDoc,
          fragment: fragment,
        }),
        editable && collaborationCursor,
        fillIntheBlanksEditorExtension,
        UniqueID.configure({
          attributeName: 'id',
          types: [fillInTheBlanksBaseExtension.name],
          generateID: () => randomId(),
          filterTransaction: (transaction) => !isChangeOrigin(transaction),
        }),
      ]),
    },
    [pinId, pinComponentId, collaborationCursor, editable, fragment]
  );

  useEffect(() => {
    if (editor) {
      // @TODO remove setTimeout whenever we are free from the shackles of Meteor
      // poor mans deselect
      setTimeout(() => editor?.chain().selectAll().blur().run(), 0);
      registerEditor?.(editor);
    }
  }, [editor, pinComponentId, registerEditor]);

  useEffect(() => {
    if (!editable) return;
    if (active) {
      teacherTextEditorStore.setEditor(editor);
      editor?.setEditable(true);
    } else {
      editor?.setEditable(false);
      setSellectedId(undefined);
    }
  }, [editable, editor, active, pinComponentId]);

  useEffect(() => {
    if (!editor) return;
    // if the cursor is on a blank, set it as selected
    const { $from } = editor.state.selection;
    const node = $from.node($from.depth);
    if (node?.type.name === fillInTheBlanksBaseExtension.name) {
      setSellectedId(node.attrs.id);
    } else {
      setSellectedId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor?.state.selection.from]);

  const distractors = useMemo(() => {
    if (isPreviewMode) return [];
    return settings.distractors ?? [];
  }, [isPreviewMode, settings.distractors]);

  const words: FillInTheBlanksWordBankBlank[] = useMemo(() => {
    if (!isPreviewMode) return blanks;
    const distractors = settings.distractors ?? [];
    const seededWordbank = seededWordbankOrder(
      blanks.map((blank) => blank.word),
      distractors,
      settings.sortingSeed
    );
    return seededWordbank.map((word) => ({
      type: 'blank',
      word: word,
    }));
  }, [isPreviewMode, blanks, settings.distractors, settings.sortingSeed]);

  const handleRemoveWord = (index: number) => {
    let i = 0;
    editor?.state.doc.descendants((node, pos) => {
      if (node.type.name === fillInTheBlanksBaseExtension.name) {
        if (i === index) {
          editor?.chain().focus().removeFillInTheBlanksWord(pos).run();
        }
        i++;
      }
    });
  };

  const handleMouseEnter = (word: FillInTheBlanksWordBankItem) => {
    if (word.type === 'blank') {
      setHoveredId(word.id);
    }
  };

  const handleMouseLeave = () => {
    setHoveredId(undefined);
  };

  useEffect(() => {
    if (sellectedId && isPreviewMode) {
      setSellectedId(undefined);
    }
  }, [sellectedId, isPreviewMode]);

  return (
    <FillInTheBlanksContextProvider
      isPreviewMode={isPreviewMode ?? false}
      hoveredId={hoveredId}
      sellectedId={sellectedId}
    >
      <AreaPinComponentWrapper>
        <TipTapWrapper backgroundColor={settings.backgroundColor}>
          <TipTapEditorContent
            className={cx('tiptap-root', textAreaStyle)}
            editor={editor}
            onMouseDown={handleMouseDown}
          />
        </TipTapWrapper>
        <WordBankWrapper>
          <FillInTheBlanksWordBankEditor
            words={words}
            pinComponentId={pinComponentId}
            distractors={distractors}
            onRemoveWord={handleRemoveWord}
            isPreviewMode={isPreviewMode}
            t={t}
            sellectedId={sellectedId}
            onWordClick={handleWordClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        </WordBankWrapper>
      </AreaPinComponentWrapper>
    </FillInTheBlanksContextProvider>
  );
};

interface FillInTheBlanksWordBankEditorProps {
  pinComponentId: string;
  words: FillInTheBlanksWordBankBlank[];
  distractors: string[];
  onRemoveWord: (index: number) => void;
  isPreviewMode?: boolean;
  t: TFunction;
  sellectedId?: string;
  onMouseEnter?: (word: FillInTheBlanksWordBankItem) => void;
  onMouseLeave?: (word: FillInTheBlanksWordBankItem) => void;
  onWordClick: (word: FillInTheBlanksWordBankBlank) => void;
}

export const FillInTheBlanksWordBankEditor: React.FC<FillInTheBlanksWordBankEditorProps> = ({
  words,
  pinComponentId,
  distractors,
  onRemoveWord,
  isPreviewMode,
  t,
  sellectedId,
  onMouseEnter,
  onMouseLeave,
  onWordClick,
}) => {
  const dispatch = useUpdatePin();

  const allWords: FillInTheBlanksWordBankItem[] = useMemo(() => {
    const wrappedDistractors: FillInTheBlanksWordBankDistractor[] = distractors.map((distractor) => ({
      type: 'distractor',
      word: distractor,
    }));
    return [...words, ...wrappedDistractors];
  }, [words, distractors]);

  const handleAddDistractor = (buttonRef: React.RefObject<HTMLButtonElement | null>) => {
    show(DistractorModal, {
      parentSelector: () => buttonRef.current,
      handleDistractorAdd,
      t,
      modalAnchor: 'start',
      direction: 'top',
    });
  };

  const handleDistractorAdd = (value: string) => {
    if (value.trim() === '') {
      return;
    }
    dispatch({
      type: 'updatePinComponentSettings',
      pinComponentId: pinComponentId,
      settings: {
        distractors: [...distractors, value],
      },
    });
  };

  const handleWordClick = (word: FillInTheBlanksWordBankItem) => {
    if (word.type === 'blank') {
      onWordClick(word);
    }
  };

  const handleRemoveWord = (word: FillInTheBlanksWordBankItem, index: number) => {
    if (word.type === 'distractor') {
      handleRemoveDistractor(word.word);
    } else {
      onRemoveWord(index);
    }
  };

  const handleRemoveDistractor = (distractor: string) => {
    dispatch({
      type: 'updatePinComponentSettings',
      pinComponentId: pinComponentId,
      settings: {
        distractors: distractors.filter((d) => d !== distractor),
      },
    });
  };

  return (
    <FillInTheBlanksWordBank
      handleWordbankAdd={words.length > 0 ? handleAddDistractor : undefined}
      items={allWords}
      onWordClick={handleWordClick}
      onRemoveWord={!isPreviewMode ? handleRemoveWord : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sellectedId={sellectedId}
    />
  );
};

interface FormInputs {
  distractor: string;
}

const DistractorModal = createModal<
  ModalPopoverProps & { handleDistractorAdd: (distractor: string) => void; t: TFunction }
>((props) => {
  const modal = useModal();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>();
  const { handleDistractorAdd, t } = props;

  const distractorSubmit = async (data: FormInputs) => {
    const distractor = data.distractor;
    if (distractor) {
      handleDistractorAdd(distractor);
    }
    modal.hide();
  };

  const getValidation = (field: keyof FormInputs): InputValidation | undefined => {
    if (errors[field]) {
      return {
        type: 'negative',
        message: errors[field]?.message || t?.('fillInTheBlanks.distractor.input.required'),
      };
    }
  };

  return (
    <ModalPopover {...props} modal={modal} contentLabel="test modal">
      <form onSubmit={handleSubmit(distractorSubmit)}>
        <SpaceBetween direction="x" spacing={spacing.size4} padding={spacing.size8}>
          <TextField
            type="text"
            size="medium"
            aria-label={t?.('fillInTheBlanks.distractor.input.label')}
            {...register('distractor', {
              maxLength: { value: 1000, message: t?.('fillInTheBlanks.distractor.input.maxLength') },
              required: t?.('fillInTheBlanks.distractor.input.required'),
            })}
            placeholder={t?.('fillInTheBlanks.distractor.input.label')}
            defaultValue=""
            autoComplete="off"
            validation={getValidation('distractor')}
          />
          <Button
            buttonType="secondary"
            size="medium"
            buttonShape="rect"
            aria-label={t?.('fillInTheBlanks.distractor.input.add')}
            type="submit"
          >
            {t?.('fillInTheBlanks.distractor.input.add')}
          </Button>
        </SpaceBetween>
      </form>
    </ModalPopover>
  );
});

const textAreaStyle = cssBase`
  padding: ${spacing.size8};
`;
